import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors } from '@angular/forms';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { OfferService } from './offer.service';

@Injectable({
    providedIn: 'root',
})
export class SkuValidatorService {
    // Map to track loading state for each SKU
    private loadingSkus = new BehaviorSubject<Map<string, boolean>>(new Map());

    constructor(private readonly offerService: OfferService) {}

    /**
     * Creates a validator function that checks if SKUs are unique within the form
     *
     * @returns A validator function that can be applied to a FormGroup or FormArray
     */
    createUniqueSkuInFormValidator(): (control: AbstractControl) => ValidationErrors | null {
        return (control: AbstractControl): ValidationErrors | null => {
            const skuControls = Object.values((control as FormGroup).controls)
                .map((c) => c.get('sku'))
                .filter((c) => c.value);
            for (const skuControl of skuControls) {
                const sku = skuControl.value;
                const skuExists = skuControls.filter((c) => c.value === sku).length > 1;
                if (skuExists) {
                    return { uniqueSku: true };
                }
            }
            return null;
        };
    }

    /**
     * Creates a validator function that checks if SKUs are already used by other offers
     *
     * @param currentOfferId The ID of the current offer being edited (to exclude from check)
     * @returns A validator function that can be applied to a FormGroup, FormArray, or FormControl
     */
    createUniqueSkuAcrossOffersValidator(currentOfferId?: string): (control: AbstractControl) => Observable<ValidationErrors | null> {
        return (control: AbstractControl): Observable<ValidationErrors | null> => {
            if (!control || !control.value) {
                return of(null);
            }

            return this.checkSkuExistsInOffers(control.value, currentOfferId);
        };
    }

    /**
     * Checks if a SKU exists in other offers
     *
     * @param sku The SKU to check
     * @param currentOfferId The ID of the current offer being edited (to exclude from check)
     * @returns Observable that emits validation errors if the SKU exists, null otherwise
     */
    private checkSkuExistsInOffers(sku: string, currentOfferId?: string): Observable<ValidationErrors | null> {
        // Set loading state to true for this SKU
        this.setSkuLoading(sku, true);

        console.log('Checking SKU:', sku);

        // Check if SKU is used by other offers debounced (before sending request to server)
        return this.offerService.checkSkuExists(sku, currentOfferId).pipe(
            debounceTime(300),
            tap((): void => {
                console.log('SKU exists:', sku);
                // Set loading state to false when validation completes
                this.setSkuLoading(sku, false);
            }),
            map((exists): ValidationErrors | null => (exists ? { skuExists: true } : null)),
            catchError((): Observable<ValidationErrors | null> => {
                // Set loading state to false on error
                this.setSkuLoading(sku, false);
                return of(null);
            }),
        );
    }

    /**
     * Checks if a SKU is currently being validated
     *
     * @param sku The SKU to check
     * @returns Observable that emits true if the SKU is being validated, false otherwise
     */
    isSkuLoading(sku: string): Observable<boolean> {
        return this.loadingSkus.pipe(
            map((loadingMap) => loadingMap.get(sku) || false),
            distinctUntilChanged(),
        );
    }

    /**
     * Sets the loading state for a SKU
     *
     * @param sku The SKU to set the loading state for
     * @param isLoading Whether the SKU is loading
     */
    private setSkuLoading(sku: string, isLoading: boolean): void {
        const currentMap = this.loadingSkus.value;
        const newMap = new Map(currentMap);

        if (isLoading) {
            newMap.set(sku, true);
        } else {
            newMap.delete(sku);
        }

        this.loadingSkus.next(newMap);
    }
}
